/**
 * /admin/api-keys — list every key the caller owns (admin sees all), create
 * new ones, copy the JWT once on creation, and revoke.
 */

import { type FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, api } from '../lib/api';
import { formatDate } from '../lib/format';
import { Badge, Banner, Icon, Mono } from '../components/brand';
import { Combobox } from '../components/Combobox';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { Modal } from '../components/Modal';

interface ApiKeyRow {
  id: string;
  name: string;
  accountId: string;
  bucket: string;
  pathPrefix: string | null;
  actions: string[];
  issuedAt: number;
  expiresAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

interface CreatedKey extends ApiKeyRow {
  jwt: string;
}

interface AccountSummary {
  id: string;
  name: string;
}

interface BucketSummary {
  name: string;
  alias: string | null;
}

const VALID_ACTIONS = ['read', 'write', 'delete'] as const;

interface FormState {
  name: string;
  accountId: string;
  bucket: string;
  pathPrefix: string;
  actions: Set<string>;
  ttlDays: number;
}

const blankForm = (): FormState => ({
  name: '',
  accountId: '',
  bucket: '',
  pathPrefix: '',
  actions: new Set(['read']),
  ttlDays: 90,
});

export function AdminApiKeys() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(blankForm);
  const [revealed, setRevealed] = useState<CreatedKey | null>(null);

  const keysQ = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => api.get<ApiKeyRow[]>('/api/api-keys'),
  });

  const accountsQ = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountSummary[]>('/api/accounts'),
    enabled: creating,
  });

  const bucketsQ = useQuery({
    queryKey: ['accounts', form.accountId, 'buckets'],
    queryFn: () => api.get<BucketSummary[]>(`/api/accounts/${form.accountId}/buckets`),
    enabled: creating && !!form.accountId,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedKey>('/api/api-keys', {
        name: form.name.trim(),
        account_id: form.accountId,
        bucket: form.bucket.trim(),
        path_prefix: form.pathPrefix.trim() || null,
        actions: Array.from(form.actions),
        ttl_seconds: Math.max(3600, form.ttlDays * 86400),
      }),
    onSuccess: (key) => {
      setRevealed(key);
      setForm(blankForm());
      setCreating(false);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  const [revokeTargets, setRevokeTargets] = useState<ApiKeyRow[] | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKeyRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const revoke = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.post<{ id: string }>(`/api/api-keys/${id}/revoke`)),
      );
      const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      if (failed.length) {
        const first = failed[0]?.reason;
        const apiErr = first as ApiError | undefined;
        throw apiErr ?? new Error(`revoke_failed: ${failed.length}/${ids.length}`);
      }
      return ids;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setRevokeTargets(null);
      setSelectedIds([]);
    },
  });

  const rotate = useMutation({
    mutationFn: (id: string) => api.post<CreatedKey>(`/api/api-keys/${id}/rotate`),
    onSuccess: (key) => {
      setRevealed(key);
      setRotateTarget(null);
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  function toggleAction(a: string) {
    setForm((f) => {
      const next = new Set(f.actions);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return { ...f, actions: next };
    });
  }

  const keys = keysQ.data ?? [];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          <h1 className="h-page">API keys</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            JWT tokens scoped to one account / bucket / prefix. Plaintext shown once at creation.{' '}
            <a
              className="bm-link"
              href="/docs/BUCKET-INTEGRATION-GUIDE.md"
              target="_blank"
              rel="noreferrer"
            >
              Client integration guide →
            </a>
          </p>
        </div>
        <button
          type="button"
          className="bm-btn bm-btn-primary"
          onClick={() => setCreating((v) => !v)}
        >
          <Icon.plus size={13} /> {creating ? 'Cancel' : 'New key'}
        </button>
      </div>

      <RevealedKeyModal created={revealed} onClose={() => setRevealed(null)} />

      {creating && (
        <form onSubmit={submit} className="bm-card" style={{ marginBottom: 20, padding: 24 }}>
          <div className="bm-row" style={{ marginBottom: 14 }}>
            <span className="h-eyebrow">New API key</span>
          </div>
          <div className="bm-col bm-gap-12">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <label className="bm-field">
                <span className="bm-field-label">Name</span>
                <input
                  className="bm-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="ci-bot · public-assets"
                  required
                />
              </label>
              <label className="bm-field">
                <span className="bm-field-label">TTL (days)</span>
                <input
                  className="bm-input mono"
                  type="number"
                  min={1}
                  max={1825}
                  value={form.ttlDays}
                  onChange={(e) => setForm((f) => ({ ...f, ttlDays: Number(e.target.value) }))}
                />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="bm-field">
                <span className="bm-field-label">Account</span>
                <Combobox
                  value={form.accountId}
                  options={(accountsQ.data ?? []).map((a) => ({
                    value: a.id,
                    label: a.name,
                    hint: a.id,
                  }))}
                  onChange={(v) => setForm((f) => ({ ...f, accountId: v, bucket: '' }))}
                  placeholder={accountsQ.isLoading ? 'Loading…' : 'Select account…'}
                />
              </div>
              <div className="bm-field">
                <span className="bm-field-label">Bucket</span>
                <Combobox
                  value={form.bucket}
                  options={(bucketsQ.data ?? []).map((b) => ({
                    value: b.name,
                    label: b.alias || b.name,
                    hint: b.alias ? b.name : undefined,
                  }))}
                  onChange={(v) => setForm((f) => ({ ...f, bucket: v }))}
                  placeholder={
                    !form.accountId
                      ? 'Pick account first'
                      : bucketsQ.isLoading
                        ? 'Loading…'
                        : 'Select bucket…'
                  }
                  emptyLabel={bucketsQ.error ? 'Failed to load buckets' : 'No buckets'}
                  disabled={!form.accountId}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="bm-field">
                <span className="bm-field-label">Actions</span>
                <div className="bm-row bm-gap-8">
                  {VALID_ACTIONS.map((a) => (
                    <button
                      type="button"
                      key={a}
                      className="bm-row bm-gap-4"
                      style={{
                        fontSize: 12.5,
                        cursor: 'pointer',
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                      }}
                      onClick={() => toggleAction(a)}
                    >
                      <span className="bm-toggle" data-on={form.actions.has(a)} />
                      <span>{a}</span>
                    </button>
                  ))}
                </div>
              </div>
              <label className="bm-field">
                <span className="bm-field-label">Path prefix (optional)</span>
                <input
                  className="bm-input mono"
                  value={form.pathPrefix}
                  onChange={(e) => setForm((f) => ({ ...f, pathPrefix: e.target.value }))}
                  placeholder="uploads/"
                />
              </label>
            </div>

            {create.error && (
              <Banner variant="danger" code={(create.error as ApiError).code ?? 'ERROR'}>
                {(create.error as Error).message}
              </Banner>
            )}

            <div className="bm-row bm-gap-8" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="bm-btn bm-btn-sm"
                style={{ color: 'var(--bm-text-muted)' }}
                onClick={() => setCreating(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bm-btn bm-btn-primary bm-btn-sm"
                disabled={create.isPending || form.actions.size === 0}
              >
                {create.isPending ? 'Generating…' : 'Generate key'}
              </button>
            </div>
          </div>
        </form>
      )}

      {keysQ.error && (
        <div style={{ marginBottom: 16 }}>
          <Banner variant="danger" code={(keysQ.error as ApiError).code ?? 'ERROR'}>
            {(keysQ.error as Error).message}
          </Banner>
        </div>
      )}

      <ApiKeysTable
        keys={keys}
        loading={keysQ.isLoading}
        onRotate={(k) => setRotateTarget(k)}
        onSelectionChange={setSelectedIds}
        onRevokeSelected={() => {
          const targets = keys.filter((k) => selectedIds.includes(k.id) && !k.revokedAt);
          if (targets.length) setRevokeTargets(targets);
        }}
        selectedCount={selectedIds.length}
      />

      <Modal
        open={!!revokeTargets}
        onClose={() => !revoke.isPending && setRevokeTargets(null)}
        title={`Revoke ${revokeTargets?.length ?? 0} API key${(revokeTargets?.length ?? 0) > 1 ? 's' : ''}`}
        staticBackdrop={revoke.isPending}
        footer={
          <>
            <button
              type="button"
              className="bm-btn bm-btn-sm"
              style={{ color: 'var(--bm-text-muted)' }}
              disabled={revoke.isPending}
              onClick={() => setRevokeTargets(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bm-btn bm-btn-danger bm-btn-sm"
              disabled={revoke.isPending}
              onClick={() => revokeTargets && revoke.mutate(revokeTargets.map((k) => k.id))}
            >
              {revoke.isPending ? 'Revoking…' : 'Revoke'}
            </button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>
          Revoke the following key{(revokeTargets?.length ?? 0) > 1 ? 's' : ''}?
        </p>
        <ul
          className="mono"
          style={{
            margin: '6px 0 0',
            paddingLeft: 20,
            fontSize: 12.5,
            color: 'var(--bm-text-soft)',
          }}
        >
          {revokeTargets?.map((k) => (
            <li key={k.id}>{k.name}</li>
          ))}
        </ul>
        <p className="muted" style={{ fontSize: 12.5, margin: '12px 0 0' }}>
          Any holder of these tokens will stop authenticating immediately. Rotate later to issue
          fresh credentials in the same scope.
        </p>
        {revoke.error && (
          <div style={{ marginTop: 12 }}>
            <Banner variant="danger" code={(revoke.error as ApiError).code ?? 'ERROR'}>
              {(revoke.error as Error).message}
            </Banner>
          </div>
        )}
      </Modal>

      <Modal
        open={!!rotateTarget}
        onClose={() => !rotate.isPending && setRotateTarget(null)}
        title="Rotate API key"
        staticBackdrop={rotate.isPending}
        footer={
          <>
            <button
              type="button"
              className="bm-btn bm-btn-sm"
              style={{ color: 'var(--bm-text-muted)' }}
              disabled={rotate.isPending}
              onClick={() => setRotateTarget(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bm-btn bm-btn-primary bm-btn-sm"
              disabled={rotate.isPending}
              onClick={() => rotateTarget && rotate.mutate(rotateTarget.id)}
            >
              {rotate.isPending ? 'Rotating…' : 'Rotate token'}
            </button>
          </>
        }
      >
        <p style={{ marginTop: 0 }}>
          Issue a new token for{' '}
          <strong style={{ color: 'var(--bm-text)' }}>{rotateTarget?.name}</strong>?
        </p>
        <p className="muted" style={{ fontSize: 12.5, margin: '8px 0 0' }}>
          Same scope (account / bucket / prefix / actions). The previous token — revoked or not —
          stops working the moment the new one is issued. The plaintext value is shown only once.
        </p>
        {rotate.error && (
          <div style={{ marginTop: 12 }}>
            <Banner variant="danger" code={(rotate.error as ApiError).code ?? 'ERROR'}>
              {(rotate.error as Error).message}
            </Banner>
          </div>
        )}
      </Modal>
    </div>
  );
}

function RevealedKeyModal({
  created,
  onClose,
}: {
  created: CreatedKey | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!created}
      onClose={onClose}
      title={created ? `API key ready — ${created.name}` : ''}
      width={640}
      footer={
        <button type="button" className="bm-btn bm-btn-primary bm-btn-sm" onClick={onClose}>
          Done
        </button>
      }
    >
      {created && <RevealedKeyBody created={created} />}
    </Modal>
  );
}

function RevealedKeyBody({ created }: { created: CreatedKey }) {
  const [copied, setCopied] = useState<string | null>(null);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://YOUR-DOMAIN';

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      window.prompt('Copy:', text);
    }
  };

  const curlList = `curl -s "${origin}/api/public/objects?prefix=&limit=50" \\
  -H "Authorization: Bearer ${created.jwt}"`;

  const curlPresignPut = `curl -s -X POST "${origin}/api/public/objects/presign-put" \\
  -H "Authorization: Bearer ${created.jwt}" \\
  -H "Content-Type: application/json" \\
  -d '{"key":"${created.pathPrefix ?? ''}example.bin","contentType":"application/octet-stream"}'`;

  const jsList = `const res = await fetch("${origin}/api/public/objects?limit=50", {
  headers: { Authorization: "Bearer ${created.jwt}" },
});
const { data } = await res.json();`;

  return (
    <>
      <p className="muted" style={{ marginTop: 0, fontSize: 12.5 }}>
        Copy the token now. It will never be shown again. The previous token (if any) stops working
        immediately.
      </p>

      <CopyBlock
        label="JWT"
        value={created.jwt}
        wrap
        copied={copied === 'jwt'}
        onCopy={() => copy('jwt', created.jwt)}
      />

      <div style={{ marginTop: 18 }}>
        <details open>
          <summary style={{ cursor: 'pointer', listStyle: 'revert' }}>
            <span className="h-eyebrow" style={{ marginLeft: 4 }}>
              Public API quick-start
            </span>
          </summary>

          <div style={{ marginTop: 10 }}>
            <p className="muted" style={{ fontSize: 12, margin: '0 0 6px' }}>
              Scope:{' '}
              <span className="mono" style={{ color: 'var(--bm-text-soft)' }}>
                {created.accountId} / {created.bucket}
                {created.pathPrefix ? ` / ${created.pathPrefix}` : ''}
              </span>
              {' · actions: '}
              <span className="mono" style={{ color: 'var(--bm-text-soft)' }}>
                {created.actions.join(', ')}
              </span>
            </p>

            <div className="h-eyebrow" style={{ fontSize: 10, marginTop: 12 }}>
              List objects (curl)
            </div>
            <CopyBlock
              value={curlList}
              copied={copied === 'curl-list'}
              onCopy={() => copy('curl-list', curlList)}
            />

            <div className="h-eyebrow" style={{ fontSize: 10, marginTop: 12 }}>
              Presign PUT (curl)
            </div>
            <CopyBlock
              value={curlPresignPut}
              copied={copied === 'curl-put'}
              onCopy={() => copy('curl-put', curlPresignPut)}
            />

            <div className="h-eyebrow" style={{ fontSize: 10, marginTop: 12 }}>
              List objects (JS / fetch)
            </div>
            <CopyBlock
              value={jsList}
              copied={copied === 'js-list'}
              onCopy={() => copy('js-list', jsList)}
            />

            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              All endpoints under <code>/api/public/</code>. Auth header:{' '}
              <code>Authorization: Bearer &lt;jwt&gt;</code>.
            </p>
          </div>
        </details>
      </div>
    </>
  );
}

function CopyBlock({
  label,
  value,
  copied,
  onCopy,
  wrap = false,
}: {
  label?: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  wrap?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--bm-surface-recess)',
        border: '1px solid var(--bm-border-soft)',
        borderRadius: 'var(--bm-r-sm)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <Mono
        style={{
          fontSize: 12,
          flex: 1,
          color: 'var(--bm-text)',
          whiteSpace: wrap ? 'pre-wrap' : 'pre',
          wordBreak: wrap ? 'break-all' : 'normal',
          overflowX: wrap ? 'visible' : 'auto',
        }}
      >
        {value}
      </Mono>
      <button
        type="button"
        className="bm-btn bm-btn-ghost bm-btn-sm"
        style={{ flexShrink: 0 }}
        onClick={onCopy}
        aria-label={label ? `Copy ${label}` : 'Copy'}
      >
        <Icon.copy size={12} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/* ─── List table ──────────────────────────────────────────────── */

type KeyState = '' | 'active' | 'expired' | 'revoked';

function keyState(k: ApiKeyRow): Exclude<KeyState, ''> {
  if (k.revokedAt) return 'revoked';
  if (k.expiresAt < Date.now()) return 'expired';
  return 'active';
}

function ApiKeysTable({
  keys,
  loading,
  onRotate,
  onSelectionChange,
  onRevokeSelected,
  selectedCount,
}: {
  keys: ApiKeyRow[];
  loading: boolean;
  onRotate: (k: ApiKeyRow) => void;
  onSelectionChange: (ids: string[]) => void;
  onRevokeSelected: () => void;
  selectedCount: number;
}) {
  const [stateFilter, setStateFilter] = useState<KeyState>('');

  const rows = stateFilter ? keys.filter((k) => keyState(k) === stateFilter) : keys;

  const columns: DataTableColumn<ApiKeyRow>[] = [
    {
      id: 'name',
      header: 'Name',
      accessor: (k) => k.name,
      sortable: true,
      cell: (k) => <span style={{ color: 'var(--bm-text)', fontWeight: 500 }}>{k.name}</span>,
    },
    {
      id: 'scope',
      header: 'Scope',
      defaultHidden: true,
      accessor: (k) => `${k.accountId}/${k.bucket}`,
      sortable: true,
      cell: (k) => (
        <span className="mono" style={{ fontSize: 12, color: 'var(--bm-text-soft)' }}>
          {k.accountId} / {k.bucket}
          {k.pathPrefix ? ` / ${k.pathPrefix}` : ''}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      defaultHidden: true,
      cell: (k) => (
        <div className="bm-row bm-gap-4">
          {k.actions.map((a) => (
            <Badge key={a} variant="neutral">
              {a}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'issued',
      header: 'Issued',
      defaultHidden: true,
      sortable: true,
      align: 'right',
      accessor: (k) => k.issuedAt,
      cell: (k) => (
        <span className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
          {formatDate(k.issuedAt)}
        </span>
      ),
    },
    {
      id: 'expires',
      header: 'Expires',
      defaultHidden: true,
      sortable: true,
      align: 'right',
      accessor: (k) => k.expiresAt,
      cell: (k) => (
        <span className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
          {formatDate(k.expiresAt)}
        </span>
      ),
    },
    {
      id: 'lastUsed',
      header: 'Last used',
      sortable: true,
      align: 'right',
      accessor: (k) => k.lastUsedAt ?? 0,
      cell: (k) => (
        <span className="mono tnum" style={{ fontSize: 12, color: 'var(--bm-text-muted)' }}>
          {k.lastUsedAt ? formatDate(k.lastUsedAt) : '—'}
        </span>
      ),
    },
    {
      id: 'state',
      header: 'State',
      sortable: true,
      align: 'right',
      width: 100,
      accessor: (k) => keyState(k),
      cell: (k) => {
        const s = keyState(k);
        if (s === 'revoked') return <Badge variant="danger">revoked</Badge>;
        if (s === 'expired') return <Badge variant="warn">expired</Badge>;
        return <Badge variant="success">active</Badge>;
      },
    },
    {
      id: 'rowActions',
      header: '',
      width: 100,
      align: 'right',
      cell: (k) => (
        <button type="button" className="bm-btn bm-btn-ghost bm-btn-sm" onClick={() => onRotate(k)}>
          Rotate
        </button>
      ),
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      rowKey={(k) => k.id}
      loading={loading}
      emptyLabel="No API keys yet."
      searchText={(k) => `${k.name} ${k.accountId} ${k.bucket} ${k.pathPrefix ?? ''}`}
      searchPlaceholder="Search name / account / bucket…"
      filters={
        <div style={{ minWidth: 140 }}>
          <Combobox
            value={stateFilter}
            options={[
              { value: '', label: 'all states' },
              { value: 'active', label: 'active' },
              { value: 'expired', label: 'expired' },
              { value: 'revoked', label: 'revoked' },
            ]}
            onChange={(v) => setStateFilter(v as KeyState)}
            placeholder="all states"
          />
        </div>
      }
      enableSelection
      isRowSelectable={(k) => !k.revokedAt}
      onSelectionChange={onSelectionChange}
      actions={
        <button
          type="button"
          className="bm-btn bm-btn-danger bm-btn-sm"
          disabled={selectedCount === 0}
          onClick={onRevokeSelected}
        >
          <Icon.trash size={12} /> Revoke
          {selectedCount > 0 ? ` (${selectedCount})` : ''}
        </button>
      }
    />
  );
}
